/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import {
    object, bool, string, func, shape,
} from 'prop-types';
import * as Joi from 'joi-browser';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import Button from '@material-ui/core/Button';
import withStyles from '@material-ui/core/styles/withStyles';

import Form from '../../common/Form';
import configENV from '../../config';
import BackButton from '../../common/BackButton';
import ChangePasswordPLA from './ChangePasswordPLA';
import { form as FormStyle } from '../../style/form';
import ErrorInline from '../../common/ErrorMessages/ErrorInline';
import EventDispatcher from '../../state/reducers/EventDispatcher';
import { getBrand } from '../../state/ducks/Common/Common-Selectors';

const { PERSIST_KEY } = configENV;
const styles = ({ typography, palette }) => FormStyle(typography, palette);

class ChangePassword extends Form {
    state = {
        data: {
            currentPassword: '',
            newPassword: '',
        },
        showPassword: {
            currentPassword: false,
            newPassword: false,
        },
        isSubmited: false,
        errors: {},
    };

    schema = {
        currentPassword: Joi.string()
            .trim()
            .regex(
                /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d$&+,:;=?@#|'<>.^*()%!-{}[\]]{6,}$/,
                'password condition at least 6 characters long with 1 number and 1 letter',
            )
            .min(6)
            .max(50)
            .required()
            .label('Current Password'),
        newPassword: Joi.string()
            .trim()
            .invalid(Joi.ref('currentPassword'))
            .regex(
                /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#*$])[A-Za-z\d$&+,:;=?@#|'<>.^*()%!-{}[\]]{8,}$/,
                'Password must be at least 8 characters long with a mix of uppercase, lowercase, numbers and symbols.',
            )
            .min(8)
            .max(50)
            .required()
            .label('New Password'),
    };

    componentDidMount = () => {
        if (typeof window !== 'undefined') {
            window.scrollTo(0, 0);
        }
    };

    // eslint-disable-next-line camelcase
    UNSAFE_componentWillReceiveProps = (props) => {
        const { isSubmited } = this.state;
        const { isReadyState } = props;
        // Reset password fields
        if (isSubmited && isReadyState) {
            const data = {
                currentPassword: '',
                newPassword: '',
            };
            this.setState({ isSubmited: false, data });
        }
    };

    doSubmit = (submitData) => {
        const { data } = this.state;
        // console.log(data);
        this.setState({ isSubmited: true }, () => {
            submitData.handleUpdatePassword(data);
        });
    };

    handleNewPasswordChange = (dataChanged) => {
        // eslint-disable-next-line react/jsx-props-no-spreading
        const data = { ...dataChanged };
        if (data.currentPassword === data.newPassword) {
            const errors = {
                newPassword:
                    'New password cannot be the same as current password.',
            };
            this.setState({ errors });
        }
    };

    handleContinueShopping = () => {
        this.props.history.push('/');
    };

    updatePasswordForm = (props) => {
        // eslint-disable-next-line object-curly-newline
        const { isReadyState, classes, handleUpdatePassword } = props;
        const submitButtonLabel = isReadyState
            ? 'Update Password'
            : 'Updating...';
        const submitData = {
            handleUpdatePassword,
        };
        return (
            <>
                <form
                    onSubmit={(event) => this.handleSubmit(event, submitData)}
                    className={`${classes.form} row margin-top-2x`}
                    disabled={!isReadyState}
                >
                    {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                    <ErrorInline {...props} />
                    <div>
                        {this.renderInput({
                            name: 'currentPassword',
                            dataTest: { field: 'my-account-current-password' },
                            label: 'Current Password',
                            type: 'password',
                            length: 50,
                            adornment: 'Show',
                            adornmentPosition: 'end',
                        })}
                    </div>
                    <div>
                        {this.renderInput({
                            name: 'newPassword',
                            label: 'New Password',
                            dataTest: { field: 'my-account-new-password' },
                            type: 'password',
                            length: 50,
                            adornment: 'Show',
                            adornmentPosition: 'end',
                            onChangeHandler: this.handleNewPasswordChange,
                        })}
                    </div>
                    <div className="margin-top-2x opacity1">
                        {this.renderButton({
                            label: submitButtonLabel,
                            className: 'buttonHD',
                            dataTest: { field: 'my-account-submit-password' },
                            onClick: null,
                            type: 'submit',
                            isValidated: true,
                            isReadyState,
                        })}
                    </div>
                </form>
            </>
        );
    };

    continueShopping = (successMessage) => (
        <>
            <div data-test="my-account-change-password-success-text" className="margin-top-2x bold">{successMessage}</div>
            <div className="margin-top-2x row">
                <Button
                    onClick={() => this.handleContinueShopping()}
                    variant="contained"
                    color="primary"
                    className="remove-recipient-btn buttonHD"
                >
                    Continue Shopping
                </Button>
            </div>
        </>
    );

    render() {
        // eslint-disable-next-line object-curly-newline
        const {
            status,
            successMessage,
            noBackButton,
            brand,
            userProfile,
        } = this.props;

        if (brand.code === 'PLA') {
            return (
                <ChangePasswordPLA brand={brand} userProfile={userProfile} />
            );
        }

        return (
            <>
                <h1>
                    {noBackButton ? '' : <BackButton />}
                    Change Password
                </h1>
                {status !== 'success' ? (
                    <p>
                        To change your password, you must first confirm your
                        current password. Once correct, then enter your new
                        password.
                    </p>
                ) : (
                    ''
                )}
                {status === 'success'
                    ? this.continueShopping(successMessage)
                    : this.updatePasswordForm(this.props)}
            </>
        );
    }
}

ChangePassword.defaultProps = {
    status: '',
    successMessage: '',
    error: {},
    noBackButton: false,
    handleUpdatePassword: () => { },
};

ChangePassword.propTypes = {
    classes: object.isRequired,
    error: shape({
        message: string,
        name: string,
    }),
    isReadyState: bool.isRequired,
    noBackButton: bool,
    status: string,
    successMessage: string,
    handleUpdatePassword: func,
    brand: shape({
        code: string.isRequired,
    }).isRequired,
};

const mapStateToProps = (state) => {
    /* istanbul ignore next */
    const newState = state[PERSIST_KEY] ? state[PERSIST_KEY] : state;
    return {
        isReadyState: newState.isReadyState,
        status: newState.status,
        successMessage: newState.successMessage,
        error: newState.error,
        toggleState: newState.toggleState,
        brand: getBrand(state),
    };
};

const mapDispatchToProps = (dispatch) => ({
    handleUpdatePassword: (data) => dispatch(EventDispatcher.handleUpdatePassword(data)),
});

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(withRouter(withStyles(styles)(ChangePassword)));
